import { Fragment, useMemo } from 'react';
import { Badge } from '@music-vine/cadence/ui';
import { type PricingPlan, usePricingCards } from '@/components/PricingPage/PricingCards/hooks/usePricingCards';
import { featuresData } from './data';
import FeatureRow from './FeatureRow';
import PlanHeaderCell from './PlanHeaderCell';
import type { FeatureCells, PlanDetails } from './types';
import type { Settings } from '@/types/settings';
export const PricingTable = ({
  currency,
  planTypes
}: {
  currency?: Settings['currency'];
  /** Subset of plan columns, e.g. Free + Pro + Business on /pricing/business. */
  planTypes?: PricingPlan['type'][];
}) => {
  const {
    pricingPlans
  } = usePricingCards(currency?.code, undefined, {
    planTypes
  });
  const planDetailsWithHandlers: PlanDetails[] = useMemo(() => pricingPlans.map(plan => ({
    key: plan.type,
    name: plan.title,
    subtitle: plan.subtitle,
    price: plan.price,
    period: plan.period,
    buttonVariant: 'brand',
    // usePricingCards owns the highlight rules (Pro as Popular, no
    // highlight at all for Business subscribers).
    isPopular: plan.isPopular,
    badgeLabel: plan.badgeLabel
  })), [pricingPlans]);
  const popularIndex = planDetailsWithHandlers.findIndex(plan => plan.isPopular);
  const popularPlan = popularIndex === -1 ? undefined : planDetailsWithHandlers[popularIndex];
  // One column per plan plus the feature-name column, matching the table.
  const columnCount = planDetailsWithHandlers.length + 1;
  const cellOrder = planDetailsWithHandlers.map(plan => plan.key.toLowerCase() as keyof FeatureCells);
  return <div data-testid="pricing-table" className="mb-20 flex justify-center px-6" data-sentry-component="PricingTable" data-sentry-source-file="index.tsx">
      <div className="relative w-full max-w-6xl items-center justify-center pt-6 pb-12">
        {popularPlan && <div
      // The highlight starts 20px above the plan name with the badge
      // straddling its top edge (DEV-6594).
      className="pointer-events-none absolute top-5 bottom-0 left-0 grid w-full" style={{
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`
      }}>
            <div className="relative rounded-xl bg-gray-50 dark:bg-gray-900" style={{
          gridColumnStart: popularIndex + 2
        }}>
              <Badge className="absolute top-0 left-1/2 hidden h-auto -translate-x-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-[3px] font-sans font-semibold text-sm uppercase leading-5 tracking-normal lg:block" size="xs" variant="brand">
                {popularPlan.badgeLabel ?? 'Popular'}
              </Badge>
            </div>
          </div>}

        <table className="relative w-full max-w-6xl table-fixed text-left">
          <thead>
            <tr>
              {/* First empty header cell */}
              <th className="w-1/5" />
              {/* Map over plan details using the PlanHeaderCell component */}
              {planDetailsWithHandlers.map(plan => <PlanHeaderCell fallbackCurrencySymbol={currency?.symbol_left} key={plan.key} plan={plan} />)}
            </tr>
          </thead>
          <tbody>
            {featuresData.map(category => <Fragment key={category.name}>
                {/* Category Header Row */}
                <tr>
                  <th className="px-4 pt-8 pb-3 text-left font-aeonik font-bold text-base text-pink-600 dark:text-white" colSpan={planDetailsWithHandlers.length + 1} scope="colgroup">
                    {category.name}
                  </th>
                </tr>
                {/* Feature Rows using the FeatureRow component */}
                {category.features.map(feature => <FeatureRow categoryName={category.name} cellOrder={cellOrder} feature={feature} key={feature.key} />)}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>;
};